import { useSnackbars } from '@customerio/pluma-components/react';
import { useCallback, useMemo, useState } from 'react';
import { useAnalytics } from '@/services/analytics';
import {
  useUpdateDataIndexAttributeMetadata,
  useUpdateDataIndexAttributeTags,
  useUpdateDataIndexEventMetadata,
  useUpdateDataIndexEventTags,
} from '@/services/db/data-index/mutations';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { parseAsBoolean, parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { getObjectTypesQueryOptions, useObjectTypes } from '@/services/db/object-type/queries';
import type { ObjectType } from '@/services/db/object-type/entity';
import { useUnfilteredPeopleCountOptional } from '@/services/db/customer/queries';
import { getObjectsQueryOptions } from '@/services/db/object/queries';
import { isEmptyString } from '@/utils/string';
import type { EnvironmentsSourceWithID, Tag } from '@customerio/hydra-ui-api';
import {
  dataIndexKeys,
  toDataIndexNarrowing,
  type AttributeScope,
  type DataIndexAttributeRow,
  type DataIndexEventRow,
  type DataIndexListFilters,
  type DataIndexTotals,
} from '@/services/db/data-index/entity';
import {
  getDataIndexAttributeFiltersQueryOptions,
  getDataIndexAttributeProfileCounts,
  getDataIndexAttributeTotalsQueryOptions,
  getDataIndexAttributeUsageQueryOptions,
  getDataIndexEventFiltersQueryOptions,
  getDataIndexEventProfileCounts,
  getDataIndexEventTotalsQueryOptions,
  getDataIndexEventUsageQueryOptions,
  getDataIndexObjectCountsQueryOptions,
  useDataIndexAttributesOptional,
  useDataIndexEventsOptional,
} from '@/services/db/data-index/queries';
import type { DataIndexFilterCatalogue, DataIndexNarrowingControls } from './utils/toolbar';
import {
  decodeScopeSelection,
  encodeScopeSelection,
  itemKindOf,
  PROFILE_SELECTION,
  scopeSelectionParams,
} from './urls';
import { parseAsIdList } from './utils/url-ids';
import {
  DataIndexLocation,
  type DataIndexAnalyticsType,
  type DataIndexItemKind,
  type DataIndexType,
} from './utils/types';

/**
 * The state behind one browser of the data index: which rows are in scope,
 * how they are narrowed, which page of them is shown, and the counts the
 * table needs beside them.
 *
 * The scope lives in the URL as the two params the page this replaces
 * declares — `objectType` and `relationship` (`app/pods/data-index/type/
 * list/route.ts:36-43`) — rather than as the one composite value the picker
 * offers. Both apps serve these URLs while the flag rolls out, so a link
 * written by one has to be read by the other, and Ember reads two.
 */

const LIST_PARSERS = {
  page: parseAsInteger.withDefault(1),
  objectType: parseAsString.withDefault(PROFILE_SELECTION),
  relationship: parseAsString,
  search: parseAsString.withDefault(''),
  inUseOnly: parseAsBoolean.withDefault(false),
  searchTagIds: parseAsIdList.withDefault([]),
  searchSourceIds: parseAsIdList.withDefault([]),
};

/**
 * The scope a list URL is asking for.
 *
 * The two params are joined back into the picker's own composite value
 * rather than decoded separately. The segment is deliberately not consulted:
 * only `attributes` and `events` reach a list, and the object browsers are
 * chosen with the picker, which writes the query. Reading the segment here
 * would pin the scope against the picker — choosing an object type's own
 * attributes on the relationship URL could never take effect.
 */
export function scopeFromUrl(objectType: string, relationship: string | null): AttributeScope {
  return decodeScopeSelection(relationship === 'true' ? `${objectType}:relationship` : objectType);
}

/** The narrowing half of the URL, which every read of the list shares. */
type NarrowingParams = {
  page: number;
  search: string;
  inUseOnly: boolean;
  searchTagIds: number[];
  searchSourceIds: number[];
};

/**
 * What the URL is asking the backend for. A narrowing nobody chose is left
 * out rather than sent as empty: an omitted parameter and a false one mean
 * the same thing to the server but are different cache slots here.
 */
function listFiltersFrom(params: NarrowingParams): DataIndexListFilters {
  const filters: DataIndexListFilters = { page: params.page };

  if (!isEmptyString(params.search)) {
    filters.search = params.search;
  }
  if (params.inUseOnly) {
    filters.inUseOnly = true;
  }
  if (params.searchTagIds.length > 0) {
    filters['searchTagIds[]'] = params.searchTagIds;
  }
  if (params.searchSourceIds.length > 0) {
    filters['sourceIds[]'] = params.searchSourceIds;
  }

  return filters;
}

/**
 * Which set of rows a list is showing.
 *
 * Row selection is keyed on it, so a choice made before a filter change does
 * not survive into a list that no longer holds what was chosen. Paging is not
 * part of it: the next page is a different window on the same set.
 *
 * The browser type leads, because the scope alone cannot tell the two apart:
 * an event browser's scope is always the profile one, which the default
 * attributes browser also has. One file route serves both, so a tab switch
 * reconciles rather than remounts, and a shared key would carry a selection
 * across — where row ids are names, and a name can exist on both sides.
 */
export function rowSetKeyFor(type: DataIndexType, scope: AttributeScope, filters: DataIndexListFilters): string {
  return JSON.stringify([type, encodeScopeSelection(scope), toDataIndexNarrowing(filters)]);
}

/**
 * Which rows a pending deletion belongs to.
 *
 * The narrowing is deliberately not part of it. Deletion is asynchronous, so
 * the backend keeps answering with the row for a while and the list hides it
 * meanwhile — but a row hidden under one narrowing is the same row under the
 * next, and keying this like a selection brought it back the moment anyone
 * touched a filter, contradicting what the page had just said.
 */
export function deletionScopeKeyFor(workspaceId: number, type: DataIndexType, scope: AttributeScope): string {
  return JSON.stringify([workspaceId, type, encodeScopeSelection(scope)]);
}

function useDataIndexScope(workspaceId: number, type: DataIndexType) {
  // Narrowing replaces, so a filter change does not fill the history; turning
  // a page pushes, so Back returns to the page it came from.
  const [params, setParams] = useQueryStates(LIST_PARSERS, { history: 'replace' });
  const isEventBrowser = type === 'events';

  const scope = useMemo<AttributeScope>(() => {
    if (isEventBrowser) {
      return { kind: 'profile' };
    }
    return scopeFromUrl(params.objectType, params.relationship);
  }, [isEventBrowser, params.objectType, params.relationship]);

  const filters = listFiltersFrom(params);

  // Every narrowing change starts the browser again from its first page: the
  // page someone was on is a page of a set that no longer exists.
  const narrowing: DataIndexNarrowingControls = {
    search: params.search,
    onSearchChange: (search) => void setParams({ page: 1, search: isEmptyString(search) ? null : search }),
    tagIds: params.searchTagIds,
    onTagIdsChange: (tagIds) => void setParams({ page: 1, searchTagIds: tagIds.length > 0 ? tagIds : null }),
    sourceIds: params.searchSourceIds,
    onSourceIdsChange: (sourceIds) =>
      void setParams({ page: 1, searchSourceIds: sourceIds.length > 0 ? sourceIds : null }),
    inUseOnly: params.inUseOnly,
    onInUseOnlyChange: (inUseOnly) => void setParams({ page: 1, inUseOnly: inUseOnly ? true : null }),
  };

  return {
    isEventBrowser,
    kind: itemKindOf(type, scope),
    scope,
    filters,
    narrowing,
    queryNarrowing: toDataIndexNarrowing(filters),
    rowSetKey: rowSetKeyFor(type, scope, filters),
    deletionScopeKey: deletionScopeKeyFor(workspaceId, type, scope),
    page: params.page,
    selection: encodeScopeSelection(scope),
    setPage: (page: number) => void setParams({ page }, { history: 'push' }),
    /** Choosing a scope starts the browser again from its first page. */
    setSelection: (value: string) => void setParams(scopeSelectionParams(value)),
  };
}

type ScopeState = ReturnType<typeof useDataIndexScope>;

/** A page of rows and the size of the set it came from, however it was read. */
type RowPage = {
  rows: readonly (DataIndexAttributeRow | DataIndexEventRow)[];
  tags: readonly Tag[];
  /**
   * Whether there is nothing to show yet, which is what the table draws
   * skeleton rows for. Not whether a read is in flight: a save refetches the
   * list it was made from, and skeletons there would blank the table under
   * the person who saved — unmounting any other editor they had open, and
   * losing what was typed in it. The page this replaces pushes the saved row
   * into its store and refetches nothing at all.
   */
  isLoading: boolean;
  totals: DataIndexTotals | null;
};

/** Nothing yet: the shape a page read has before its first response lands. */
const EMPTY_PAGE = { rows: [], tags: [] } satisfies Pick<RowPage, 'rows' | 'tags'>;

function useAttributePage(
  workspaceId: number,
  { isEventBrowser, scope, filters, queryNarrowing }: ScopeState,
): RowPage {
  const enabled = !isEventBrowser;
  const page = useDataIndexAttributesOptional({ scope, filters, enabled });
  const totals = useQuery({
    ...getDataIndexAttributeTotalsQueryOptions(workspaceId, scope, queryNarrowing),
    enabled,
  });
  const data = page.data == null ? EMPTY_PAGE : { rows: page.data.attributes, tags: page.data.tags };

  return { ...data, isLoading: page.isPending, totals: totals.data ?? null };
}

function useEventPage(workspaceId: number, { isEventBrowser, filters, queryNarrowing }: ScopeState): RowPage {
  const page = useDataIndexEventsOptional({ filters, enabled: isEventBrowser });
  const totals = useQuery({
    ...getDataIndexEventTotalsQueryOptions(workspaceId, queryNarrowing),
    enabled: isEventBrowser,
  });
  const data = page.data == null ? EMPTY_PAGE : { rows: page.data.events, tags: page.data.tags };

  return { ...data, isLoading: page.isPending, totals: totals.data ?? null };
}

/** The page of rows in scope, and how many rows the scope holds in total. */
function useDataIndexRows(workspaceId: number, scope: ScopeState): RowPage {
  const attributes = useAttributePage(workspaceId, scope);
  const events = useEventPage(workspaceId, scope);
  return scope.isEventBrowser ? events : attributes;
}

/** Nothing to offer: what a catalogue reads as before it lands, or after it fails. */
const NO_TAGS: readonly Tag[] = [];
const NO_SOURCES: readonly EnvironmentsSourceWithID[] = [];

/**
 * Every tag and source the rows in scope were written with, which is what the
 * filter bar offers. A workspace gains these on the order of days, so unlike
 * the rows they are worth caching for a while.
 *
 * A catalogue that fails to load leaves the bar with nothing to offer, which
 * is the page it replaces answer too: not being able to narrow the list is
 * better than not being able to read it.
 */
function useDataIndexFilterCatalogue(
  workspaceId: number,
  { isEventBrowser, scope }: ScopeState,
): DataIndexFilterCatalogue {
  const attributes = useQuery({
    ...getDataIndexAttributeFiltersQueryOptions(workspaceId, scope),
    enabled: !isEventBrowser,
  });
  const events = useQuery({
    ...getDataIndexEventFiltersQueryOptions(workspaceId),
    enabled: isEventBrowser,
  });
  const answer = isEventBrowser ? events.data : attributes.data;

  // Memoised: the table rebuilds its whole filter context from this, so a
  // fresh object each render re-renders every row with it.
  return useMemo(
    () => ({ tags: answer?.tags ?? NO_TAGS, sources: answer?.sources ?? NO_SOURCES }),
    [answer?.tags, answer?.sources],
  );
}

/**
 * How much of the workspace carries each row, and what that is a share of.
 *
 * Only a person attribute or an event is carried by people, and only an
 * object's own attributes are carried by its objects, so each pair of reads
 * runs for exactly one scope.
 */
function useDataIndexCounts(workspaceId: number, { isEventBrowser, scope }: ScopeState, names: readonly string[]) {
  const isObjectScope = scope.kind === 'object';
  const objectTypeId = scope.kind === 'profile' ? 0 : scope.objectTypeId;
  const withProfileCounts = names.length > 0 && scope.kind === 'profile';

  const attributeProfileCounts = useProfileCounts(
    dataIndexKeys.attributeProfileCounts(workspaceId, names),
    names,
    withProfileCounts && !isEventBrowser,
    (asked) => getDataIndexAttributeProfileCounts(workspaceId, asked),
  );
  const eventProfileCounts = useProfileCounts(
    dataIndexKeys.eventProfileCounts(workspaceId, names),
    names,
    withProfileCounts && isEventBrowser,
    (asked) => getDataIndexEventProfileCounts(workspaceId, asked),
  );
  const objectCounts = useQuery({
    ...getDataIndexObjectCountsQueryOptions(workspaceId, objectTypeId),
    enabled: isObjectScope,
  });
  const denominators = useDataIndexDenominators(workspaceId, scope);

  const profileCounts = isEventBrowser ? eventProfileCounts : attributeProfileCounts;

  return {
    profileCounts: profileCounts.data,
    // The object-count endpoint answers for every attribute of the type in one
    // pass, so a name it omits has no objects rather than an unknown count.
    objectCounts: objectCounts.data,
    // A count still being read is not a count the backend could not reach.
    // The cell shows a skeleton for the first and a dash for the second; the
    // page this replaces draws the same distinction, row by row: the column
    // waits for the denominator every share divides by, and a row waits for
    // its own count only while the rounds that fill it are still running.
    isTotalPeopleLoading: denominators.isTotalPeopleLoading,
    isProfileCountsFetching: profileCounts.isFetching && withProfileCounts,
    // One call answers for every attribute of the type, so there is nothing
    // to fill in round by round on this side.
    isObjectCountsLoading: (objectCounts.isFetching && isObjectScope) || denominators.isTotalObjectsLoading,
    totalPeople: denominators.totalPeople,
    totalObjects: denominators.totalObjects,
  };
}

/**
 * How many rounds of profile counts to ask for before giving up.
 *
 * The endpoint answers within a time budget and omits the names it could not
 * reach, so one request routinely comes back short. Asking again for only the
 * missing names fills the gaps; a round that makes no progress means the rest
 * are not coming, and asking again would loop.
 */
const MAX_PROFILE_COUNT_ROUNDS = 6;

/** @internal exported for its test; the page reads it through `useProfileCounts`. */
export async function fetchProfileCountsInRounds(
  names: readonly string[],
  fetchRound: (names: readonly string[]) => Promise<Record<string, number>>,
  onRound?: (counts: Record<string, number>) => void,
): Promise<Record<string, number>> {
  const counts: Record<string, number> = {};
  let missing = [...names];

  for (let round = 0; round < MAX_PROFILE_COUNT_ROUNDS && missing.length > 0; round += 1) {
    Object.assign(counts, await fetchRound(missing));
    onRound?.({ ...counts });
    const remaining = missing.filter((name) => counts[name] === undefined);
    if (remaining.length === missing.length) {
      break;
    }
    missing = remaining;
  }

  return counts;
}

function useProfileCounts(
  queryKey: readonly unknown[],
  names: readonly string[],
  enabled: boolean,
  fetchRound: (names: readonly string[]) => Promise<Record<string, number>>,
) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey,
    // Each round is published as it lands, so the cells fill in as the
    // backend answers rather than all at once after as many as six requests.
    // The page this replaces assigns its counts the same way.
    queryFn: () => {
      // A new run starts from nothing, so a row that the last run answered
      // goes back to a skeleton rather than showing a count this run has not
      // confirmed. The page this replaces clears its counts the same way.
      queryClient.setQueryData(queryKey, {});
      return fetchProfileCountsInRounds(names, fetchRound, (counts) => queryClient.setQueryData(queryKey, counts));
    },
    enabled: enabled && names.length > 0,
    staleTime: 0,
  });
}

/**
 * What the percentage columns divide by. Each is a whole-list read whose only
 * interesting field is its total, which is what the page it replaces did too.
 */
function useDataIndexDenominators(workspaceId: number, scope: AttributeScope) {
  const queryClient = useQueryClient();
  const objectTypeId = scope.kind === 'profile' ? 0 : scope.objectTypeId;

  const totalPeople = useUnfilteredPeopleCountOptional({ workspaceId, enabled: scope.kind === 'profile' });
  const totalObjects = useQuery({
    ...getObjectsQueryOptions(workspaceId, { objectTypeId: String(objectTypeId) }, queryClient),
    enabled: scope.kind === 'object',
    select: (list) => list.meta?.pagination.total ?? 0,
  });

  return {
    totalPeople: totalPeople.data ?? 0,
    totalObjects: totalObjects.data ?? 0,
    // Fetching, not pending: a refetch is a read in flight whether or not a
    // stale total is cached, and showing the old denominator against new
    // counts is a percentage that was never true. The page this replaces
    // reads the task's running state for the same reason.
    isTotalPeopleLoading: totalPeople.isFetching && scope.kind === 'profile',
    isTotalObjectsLoading: totalObjects.isFetching && scope.kind === 'object',
  };
}

/**
 * Where each row on the page is used.
 *
 * Its own read, because it answers for the names on this page rather than
 * for the set the list is showing: turning a page asks again, and the row
 * reads do not wait for it. Only attributes and events have usage; the
 * endpoint differs between them and the scope picks it.
 */
function useDataIndexRowUsage(workspaceId: number, scope: ScopeState, names: readonly string[]) {
  const isEvent = scope.isEventBrowser;
  const attributes = useQuery({
    ...getDataIndexAttributeUsageQueryOptions(workspaceId, scope.scope, names),
    enabled: !isEvent && names.length > 0,
  });
  const events = useQuery({
    ...getDataIndexEventUsageQueryOptions(workspaceId, names),
    enabled: isEvent && names.length > 0,
  });
  const usage = isEvent ? events : attributes;

  return {
    rowUsage: usage.data,
    // A failed read is not a loading read: the cell says "-" rather than
    // spinning forever over counts that are never coming.
    isRowUsageLoading: usage.isPending && names.length > 0,
  };
}

export function useDataIndexList(type: DataIndexType) {
  const workspaceId = requireWorkspaceId();
  const scope = useDataIndexScope(workspaceId, type);
  const list = useDataIndexRows(workspaceId, scope);
  const catalogue = useDataIndexFilterCatalogue(workspaceId, scope);
  const names = useMemo(() => list.rows.map((row) => row.id), [list.rows]);
  const counts = useDataIndexCounts(workspaceId, scope, names);
  const usage = useDataIndexRowUsage(workspaceId, scope, names);

  return {
    workspaceId,
    isEventBrowser: scope.isEventBrowser,
    kind: scope.kind,
    scope: scope.scope,
    scopeSelection: scope.selection,
    setScopeSelection: scope.setSelection,
    rowSetKey: scope.rowSetKey,
    deletionScopeKey: scope.deletionScopeKey,
    narrowing: scope.narrowing,
    catalogue,
    page: scope.page,
    setPage: scope.setPage,
    ...list,
    ...counts,
    ...usage,
  };
}

function enabledObjectTypes(objectTypes: readonly ObjectType[]): ObjectType[] {
  return objectTypes.filter((objectType) => objectType.enabled).sort((a, b) => a.name.localeCompare(b.name));
}

/** The object types a workspace has enabled, in the order the picker lists them. */
export function useEnabledObjectTypes() {
  const { data } = useObjectTypes();
  return useMemo(() => enabledObjectTypes(data), [data]);
}

/**
 * The same list, read without suspending. The page header is chrome and has
 * to paint before the list it labels, so the export menu fills in its object
 * types rather than holding the whole page back for them.
 */
export function useEnabledObjectTypesOptional() {
  const workspaceId = requireWorkspaceId();
  const queryClient = useQueryClient();
  const { data } = useQuery(getObjectTypesQueryOptions(workspaceId, queryClient));
  return useMemo(() => enabledObjectTypes(data ?? []), [data]);
}

type Selection = { key: string; rows: Record<string, boolean> };

/** Row selection, reset whenever the rows underneath it change. */
export function useRowSelection(selectionKey: string) {
  const [selection, setSelection] = useState<Selection>({ key: selectionKey, rows: {} });
  const rows = selection.key === selectionKey ? selection.rows : {};

  return {
    rows,
    setRows: (next: Record<string, boolean>) => setSelection({ key: selectionKey, rows: next }),
  };
}

type DeletedRows = { key: string; names: readonly string[] };

const EMPTY_NAMES: readonly string[] = [];

/**
 * Rows the backend has been asked to delete, which it still returns for a
 * while. The list hides them so it agrees with what was just asked for.
 *
 * Keyed like the selection, and for the same reason: a name identifies a row
 * only within one scope, so `plan` deleted from people must not vanish from
 * an object's attributes.
 */
export function useDeletedRows(rowSetKey: string) {
  const [deleted, setDeleted] = useState<DeletedRows>({ key: rowSetKey, names: [] });
  const names = deleted.key === rowSetKey ? deleted.names : EMPTY_NAMES;

  return {
    names,
    // An updater, so two answers landing in one render keep both sets of
    // names rather than the second dropping the first.
    add: (added: readonly string[]) =>
      setDeleted((current) => ({
        key: rowSetKey,
        names: current.key === rowSetKey ? [...current.names, ...added] : [...added],
      })),
  };
}

/**
 * Saving one row's description or tags from the list.
 *
 * Every write exists in two families — an attribute's and an event's — and
 * an attribute's again in three scopes, all of which the data layer resolves
 * from the scope. What is left here is what the list adds: saying whether it
 * worked, and recording that it was done from the table rather than from the
 * row's own page.
 */
export function useDataIndexRowWrites(workspaceId: number, kind: DataIndexItemKind, scope: AttributeScope) {
  const snackbars = useSnackbars();
  const analytics = useAnalytics();
  const isEvent = kind === 'event';
  const type: DataIndexAnalyticsType = isEvent ? 'event' : 'attribute';
  const noun = isEvent ? 'event' : 'attribute';

  // Only the mutate functions, which keep their identity. A mutation result
  // is rebuilt whenever its state changes, so depending on the whole object
  // would give these callbacks a new identity the instant a save goes
  // pending — remounting the very editor that is saving, and losing what was
  // typed if the write is refused.
  const { mutateAsync: writeAttributeMetadata } = useUpdateDataIndexAttributeMetadata({ workspaceId });
  const { mutateAsync: writeEventMetadata } = useUpdateDataIndexEventMetadata({ workspaceId });
  const { mutateAsync: writeAttributeTags } = useUpdateDataIndexAttributeTags({ workspaceId });
  const { mutateAsync: writeEventTags } = useUpdateDataIndexEventTags({ workspaceId });

  /** Report what became of a save, and tell the caller whether to close. */
  const report = useCallback(
    async (write: Promise<unknown>): Promise<boolean> => {
      try {
        await write;
      } catch {
        snackbars.create({ variant: 'critical', message: `Failed to update ${noun}` });
        return false;
      }
      snackbars.create({ variant: 'success', message: `Successfully updated ${noun}` });
      return true;
    },
    [noun, snackbars],
  );

  const saveDescription = useCallback(
    async (name: string, description: string): Promise<boolean> => {
      const saved = await report(
        isEvent ? writeEventMetadata({ name, description }) : writeAttributeMetadata({ scope, name, description }),
      );
      // Only a save that landed is an update; a refused one counted as well
      // before this awaited.
      if (saved) {
        void analytics.track('dataIndex', 'descriptionUpdated', { type, location: DataIndexLocation.Table });
      }
      return saved;
    },
    [analytics, writeAttributeMetadata, writeEventMetadata, isEvent, report, scope, type],
  );

  const saveTags = useCallback(
    async (name: string, tagIds: number[]): Promise<boolean> => {
      const saved = await report(
        isEvent ? writeEventTags({ name, tagIds }) : writeAttributeTags({ scope, name, tagIds }),
      );
      if (saved) {
        void analytics.track('dataIndex', 'tagsUpdated', {
          type,
          location: DataIndexLocation.Table,
          tag_count: tagIds.length,
        });
      }
      return saved;
    },
    [analytics, writeAttributeTags, writeEventTags, isEvent, report, scope, type],
  );

  // Stable, because the table's column definitions are memoised on them.
  return useMemo(() => ({ saveDescription, saveTags }), [saveDescription, saveTags]);
}
